import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import {
  Paper,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import axiosInstance from "../api/axiosConfig";
import userImg from "../assets/icons8-user-50.png";
import { formatDateTime, formatDateOnly } from "../utils/formatters";
import {
  hasDevSiteUrl,
  isDevSite,
  readDevSiteOverrides,
  restoreDevSiteOverrides,
  useDevSiteOverrides,
} from "../utils/devSites";
import { isExpired, isPaidPlan } from "../utils/subscription";
import { devSiteColumn, devSiteRowProps } from "../components/DevSiteSwitch";
import { expiredRowClass, planStatusColumn } from "../components/PlanStatus";
import SettingsDialog from "../components/SettingsDialog";
import "./NewInstallations.css";

const NewInstallation = () => {
  const [installations, setInstallations] = useState([]);
  const [installationStats, setInstallationStats] = useState(null);
  const [weeklyUpdates, setWeeklyUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [hideDevSites, setHideDevSites] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isChartExpanded, setIsChartExpanded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const navigate = useNavigate();
  const devSiteOverrides = useDevSiteOverrides();

  useEffect(() => {
    if (localStorage.getItem("isAuthenticated") !== "true") {
      navigate("/admin-login");
      return;
    }

    const fetchData = async () => {
      try {
        const [installationsRes, installationInfoRes, updatesRes] =
          await Promise.all([
            axiosInstance.get("/admin/installations"),
            axiosInstance.get("/Installation"),
            axiosInstance.get("/last-week-updates"),
          ]);

        setInstallations(installationsRes.data.installations || []);
        setInstallationStats(installationInfoRes.data);
        setWeeklyUpdates(updatesRes.data.updates || []);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, reloadKey]);

  const rows = useMemo(
    () =>
      [...installations]
        .sort((a, b) => b.id - a.id)
        .map((item, index) => ({
          id: item.id ?? index,
          instanceId: item.instanceId || "—",
          installed_at: formatDateTime(item.installed_at),
          reinstalled_at: formatDateTime(item.reinstalled_at),
          uninstalled_at: formatDateTime(item.uninstalled_at),
          app_installed: item.app_installed === 1 ? "Yes" : "No",
          plan: item.plan ?? "—",
          expires_on: formatDateTime(item.expires_on),
          cycle: item.cycle ?? "—",
          url: item.url ?? "—",
          auto_renew:
            item.auto_renew === 1 ? "Yes" : item.auto_renew === 0 ? "No" : "—",
          cancelReason: item.cancelReason ?? "—",
          userReason: item.userReason ?? "—",
          subscriptionCancellationType:
            item.subscriptionCancellationType ?? "—",
          site_name: item.site_name ?? "—",
          plan_purchase_at: formatDateTime(item.plan_purchase_at),
          is_dev_site: isDevSite(item, devSiteOverrides),
          is_expired: isExpired(item),
          // Raw values for sorting
          raw_installed_at: item.installed_at,
          raw_reinstalled_at: item.reinstalled_at,
          raw_uninstalled_at: item.uninstalled_at,
          raw_expires_on: item.expires_on,
          raw_plan_purchase_at: item.plan_purchase_at,
          raw: item,
        })),
    [installations, devSiteOverrides],
  );

  // Development sites are excluded from every count on this page.
  const countedRows = useMemo(
    () => rows.filter((row) => !row.is_dev_site),
    [rows],
  );

  const devRows = useMemo(() => rows.filter((row) => row.is_dev_site), [rows]);

  const installationInfo = useMemo(() => {
    // The backend totals count every site, including the ones this dashboard
    // does not treat as real paying customers, so they are subtracted here.
    const lessExcluded = (total, excludedCount) =>
      Math.max(0, (Number(total) || 0) - excludedCount);

    const today = formatDateOnly(new Date());
    const onDate = (value) => value && formatDateOnly(value) === today;

    return {
      total_installed: lessExcluded(
        installationStats?.total_installed,
        devRows.length,
      ),
      active_installed: lessExcluded(
        installationStats?.active_installed,
        devRows.filter((row) => row.app_installed === "Yes").length,
      ),
      // A plan that has passed its expiry is no longer a purchase in effect.
      plan_purchased: lessExcluded(
        installationStats?.plan_purchased,
        devRows.filter((row) => isPaidPlan(row.plan)).length +
          countedRows.filter((row) => isPaidPlan(row.plan) && row.is_expired)
            .length,
      ),
      today_installed: countedRows.filter((row) => onDate(row.raw_installed_at))
        .length,
      today_paid_users: countedRows.filter(
        (row) =>
          isPaidPlan(row.plan) &&
          !row.is_expired &&
          onDate(row.raw_installed_at),
      ).length,
      today_uninstalled: countedRows.filter((row) =>
        onDate(row.raw_uninstalled_at),
      ).length,
    };
  }, [installationStats, countedRows, devRows]);

  const updateStats = useMemo(() => {
    const devInstanceIds = new Set(
      devRows.map((row) => row.raw?.instanceId).filter(Boolean),
    );

    const dateCounts = weeklyUpdates.reduce((acc, curr) => {
      if (!curr.installedAt) return acc;
      const isDev =
        hasDevSiteUrl(curr) ||
        (curr.instanceId && devInstanceIds.has(curr.instanceId));
      if (isDev) return acc;
      const date = formatDateOnly(new Date(curr.installedAt));
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const now = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(now);
      d.setDate(d.getDate() - (6 - i));
      const date = formatDateOnly(d);
      return { date, count: dateCounts[date] || 0 };
    });
  }, [weeklyUpdates, devRows]);

  const visibleRows = hideDevSites ? countedRows : rows;

  const filteredRows =
    searchQuery.trim() === ""
      ? visibleRows
      : visibleRows.filter((row) => {
          const lowerQuery = searchQuery.trim().toLowerCase();
          return (
            String(row.id).toLowerCase().includes(lowerQuery) ||
            String(row.url).toLowerCase().includes(lowerQuery) ||
            String(row.site_name).toLowerCase().includes(lowerQuery)
          );
        });

  const handleRowClick = (row) => {
    if (row.original.app_installed === "Yes") {
      navigate(`/details/${row.original.id}`);
    }
  };

  const handleLogout = () => {
    // Dev site marks are configuration, not session state, so keep them.
    const devSiteOverridesToKeep = readDevSiteOverrides();
    localStorage.clear();
    sessionStorage.clear();
    restoreDevSiteOverrides(devSiteOverridesToKeep);
    navigate("/admin-login");
    window.location.reload();
  };

  const xAxisLabels = updateStats.map((u) => {
    if (!u.date || u.date.length < 10) return "Unknown";
    return `${u.date.slice(8, 10)}-${u.date.slice(5, 7)}`;
  });

  const columns = [
    {
      accessorFn: (row, index) => index + 1,
      id: "serial",
      header: "ID",
      size: 70,
      Cell: ({ row }) => row.index + 1,
      enableSorting: false, // Disable sorting for serial number
    },
    {
      accessorKey: "instanceId",
      header: "Instance ID",
      size: 140,
      muiTableBodyCellProps: { className: "mrt-cell-instanceId" },
    },
    {
      accessorFn: (row) => new Date(row.raw_installed_at || 0),
      id: "installed_at",
      header: "Installed At",
      size: 175,
      Cell: ({ row }) => row.original.installed_at,
      sortingFn: "datetime",
    },
    {
      accessorFn: (row) => new Date(row.raw_reinstalled_at || 0),
      id: "reinstalled_at",
      header: "Reinstalled At",
      size: 175,
      Cell: ({ row }) => row.original.reinstalled_at,
      sortingFn: "datetime",
    },
    {
      accessorFn: (row) => new Date(row.raw_uninstalled_at || 0),
      id: "uninstalled_at",
      header: "Uninstalled At",
      size: 175,
      Cell: ({ row }) => row.original.uninstalled_at,
      sortingFn: "datetime",
    },
    {
      accessorKey: "app_installed",
      header: "App Installed",
      size: 110,
    },
    devSiteColumn,
    {
      accessorKey: "plan",
      header: "Plan",
      size: 100,
    },
    planStatusColumn,
    {
      accessorFn: (row) => new Date(row.raw_expires_on || 0),
      id: "expires_on",
      header: "Expires On",
      size: 175,
      Cell: ({ row }) => row.original.expires_on,
      sortingFn: "datetime",
    },
    {
      accessorKey: "cycle",
      header: "Cycle",
      size: 100,
    },
    {
      accessorKey: "url",
      header: "URL",
      size: 150,
      muiTableBodyCellProps: { className: "mrt-cell-url" },
      Cell: ({ cell }) =>
        cell.getValue() !== "—" ? (
          <a
            href={cell.getValue()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="paid-users-link"
          >
            {cell.getValue()}
          </a>
        ) : (
          <span>—</span>
        ),
    },
    {
      accessorKey: "auto_renew",
      header: "Auto Renew",
      size: 100,
    },
    {
      accessorKey: "cancelReason",
      header: "Cancel Reason",
      size: 150,
    },
    {
      accessorKey: "userReason",
      header: "User Reason",
      size: 150,
    },
    {
      accessorKey: "subscriptionCancellationType",
      header: "Cancellation Type",
      size: 160,
    },
    {
      accessorKey: "site_name",
      header: "Site Name",
      size: 170,
    },
    {
      accessorFn: (row) => new Date(row.raw_plan_purchase_at || 0),
      id: "plan_purchase_at",
      header: "Plan Purchased At",
      size: 175,
      Cell: ({ row }) => row.original.plan_purchase_at,
      sortingFn: "datetime",
    },
  ];

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        className="new-installations-container"
        sx={{
          transition: "filter 0.3s ease",
          filter: isChartExpanded ? "blur(4px)" : "none",
        }}
      >
        <Box className="top-heading">
          <Typography variant="h6" className="page-title">
            App Installation
          </Typography>
          <Box className="user-section">
            <img src={userImg} alt="User" className="user-img" />
            <span className="username">Jay Mehta</span>
            <Tooltip title="Settings">
              <IconButton
                aria-label="Settings"
                className="settings-button"
                onClick={() => setIsSettingsOpen(true)}
                sx={{ color: "white" }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          </Box>
        </Box>

        <Box className="dashboard-overview">
          <Box className="overview-cards">
            <div className="stat-card">
              <p>Total Installed</p>
              <strong>{installationInfo?.total_installed || 0}</strong>
            </div>
            <div className="stat-card">
              <p>Active Installed</p>
              <strong>{installationInfo?.active_installed || 0}</strong>
            </div>
            <div className="stat-card">
              <p>Plan Purchased</p>
              <strong>{installationInfo?.plan_purchased || 0}</strong>
            </div>
            <Box className="today-activity-box">
              <Typography variant="h6" className="activity-title">
                Today Activity
              </Typography>
              <ol className="activity-list">
                <li>
                  <span className="bullet">→</span>{" "}
                  {installationInfo?.today_installed || 0} App Installed
                </li>
                <li>
                  <span className="bullet">→</span>{" "}
                  {installationInfo?.today_paid_users || 0} Paid Users Added
                </li>
                <li>
                  <span className="bullet">→</span>{" "}
                  {installationInfo?.today_uninstalled || 0} Uninstalled
                </li>
              </ol>
            </Box>
          </Box>

          {updateStats.length > 0 && (
            <Box className="chart-container">
              <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}>
                <OpenInNewIcon
                  sx={{ cursor: "pointer", color: "#ffffff" }}
                  onClick={() => setIsChartExpanded(true)}
                />
              </Box>
              <LineChart
                margin={{ left: 10, right: 26, top: 10, bottom: 5 }}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  "& .MuiChartsAxis-tickLabel tspan": { fill: "#ffffff" },
                  "& .MuiChartsAxis-label tspan": { fill: "#ffffff" },
                  "& .MuiChartsLegend-root text": { fill: "#ffffff" },
                  "& .MuiChartsTooltip-root, & .MuiChartsTooltip-root *": {
                    color: "#ffffff",
                  },
                }}
                series={[
                  {
                    data: updateStats.map((u) => u.count || 0),
                    label: "Installations",
                    color: "#ffffff",
                  },
                ]}
                xAxis={[
                  {
                    scaleType: "point",
                    data: xAxisLabels,
                  },
                ]}
              />
            </Box>
          )}
        </Box>

        <Box className="filter-bar">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={hideDevSites}
                onChange={(e) => setHideDevSites(e.target.checked)}
              />
            }
            label={`Hide dev sites (${devRows.length})`}
            className="dev-site-filter"
          />
          <button
            className="paid-button"
            onClick={() => navigate("/paid-users")}
          >
            Paid Users
          </button>
          <button
            className="cancelled-button"
            onClick={() => navigate("/cancelled-users")}
          >
            Cancelled Users
          </button>
          <input
            type="text"
            placeholder="Search"
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Box>

        {filteredRows.length === 0 && searchQuery.trim() !== "" && (
          <Typography sx={{ mt: 2, textAlign: "center", color: "grey.600" }}>
            No results found for "{searchQuery}"
          </Typography>
        )}

        <Box className="table-wrapper">
          <Paper
            elevation={2}
            sx={{ height: "100%", width: "100%" }}
            className="custom-data-grid"
          >
            <MaterialReactTable
              columns={columns}
              data={filteredRows}
              state={{ isLoading: loading }}
              enableColumnOrdering
              enablePagination
              enableSorting
              enableBottomToolbar
              enableColumnResizing
              columnResizeMode="onChange"
              muiTableContainerProps={{ sx: { height: "100%" } }}
              initialState={{ pagination: { pageSize: 100 } }}
              muiTableBodyRowProps={({ row }) => ({
                className: [
                  devSiteRowProps(row).className,
                  expiredRowClass(row),
                ]
                  .filter(Boolean)
                  .join(" "),
                onClick: () => handleRowClick(row),
                sx: {
                  cursor:
                    row.original.app_installed === "Yes"
                      ? "pointer"
                      : "default",
                  backgroundColor:
                    row.original.app_installed === "Yes"
                      ? "inherit"
                      : "#f5f5f5",
                },
              })}
            />
          </Paper>
        </Box>
      </Box>

      <SettingsDialog
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onSynced={() => setReloadKey((key) => key + 1)}
      />

      {isChartExpanded && (
        <>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(0, 0, 0, 0.6)",
              zIndex: 1100,
            }}
            onClick={() => setIsChartExpanded(false)}
          />
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 700,
              height: 400,
              bgcolor: "white",
              zIndex: 1200,
              p: 2,
              boxShadow: 3,
              borderRadius: 2,
            }}
          >
            <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
              <CloseIcon
                sx={{ cursor: "pointer", color: "grey.600" }}
                onClick={() => setIsChartExpanded(false)}
              />
            </Box>
            <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
              Last 7 Days App Installations
            </Typography>
            <LineChart
              width={730}
              height={360}
              margin={{ right: 90 }}
              series={[
                {
                  data: updateStats.map((u) => u.count || 0),
                  label: "Installations",
                },
              ]}
              xAxis={[
                {
                  scaleType: "point",
                  data: xAxisLabels,
                },
              ]}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default NewInstallation;
